import React, { useState, useEffect } from 'react';
import { FaTimes, FaEdit, FaTrash, FaUsers, FaLock, FaSpinner } from 'react-icons/fa';
import { updateLayoutConfig, deleteLayoutConfig, getLayoutConfigs } from '../utils/layoutConfigStorage';
import { useAuth } from '../contexts/AuthContext';
import { isAdminUser } from '../utils/admin';

export default function ManageLayoutsModal({ isOpen, onClose, onLayoutsChange }) {
  const { user } = useAuth();
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  const isAdmin = isAdminUser(user, userProfile);
  const userId = user?.uid;

  useEffect(() => {
    if (isOpen && user) {
      loadUserProfile();
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (isOpen) {
      loadLayouts();
    }
  }, [isOpen, user, userProfile, isAdmin]);

  const loadUserProfile = async () => {
    if (!user?.uid) return;
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      const profileSnap = await getDoc(doc(db, 'users', user.uid));
      setUserProfile(profileSnap.exists() ? profileSnap.data() : null);
    } catch (error) {
      setUserProfile(null);
    }
  };

  const loadLayouts = async () => {
    try {
      setLoading(true);
      const configs = await getLayoutConfigs(userId, isAdmin);
      setLayouts(configs);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load layouts:', error);
      }
      alert('Failed to load layouts: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (layout) => {
    setEditingId(layout.id);
    setEditName(layout.name);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleSaveEdit = async (layoutId) => {
    if (!editName.trim()) return;

    const layout = layouts.find((l) => l.id === layoutId);
    if (layout && layout.is_public && !isAdmin && layout.author_id !== userId) {
      alert('You cannot edit public layouts created by others. Only admins can modify public layouts.');
      return;
    }

    try {
      setSaving(true);
      await updateLayoutConfig(layoutId, { name: editName.trim() });
      await loadLayouts();
      setEditingId(null);
      setEditName('');
      if (onLayoutsChange) onLayoutsChange();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to update layout:', error);
      }
      alert('Failed to update layout: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (layoutId, layoutName) => {
    const layout = layouts.find((l) => l.id === layoutId);
    if (layout && layout.is_public && !isAdmin && layout.author_id !== userId) {
      alert('You cannot delete public layouts created by others. Only admins can delete public layouts.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${layoutName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingId(layoutId);
      await deleteLayoutConfig(layoutId);
      await loadLayouts();
      if (onLayoutsChange) onLayoutsChange();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to delete layout:', error);
      }
      alert('Failed to delete layout: ' + error.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleTogglePublic = async (layout) => {
    if (!isAdmin) return;

    try {
      setSaving(true);
      await updateLayoutConfig(layout.id, { is_public: !layout.is_public });
      await loadLayouts();
      if (onLayoutsChange) onLayoutsChange();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to update layout visibility:', error);
      }
      alert('Failed to update layout visibility: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving && !deletingId) {
      setEditingId(null);
      setEditName('');
      onClose();
    }
  };

  const visibleLayouts = layouts.filter((layout) => {
    if (isAdmin) return true;
    return layout.is_public || layout.author_id === userId;
  });

  if (!isOpen) return null;

  return (
    <div className="add-metrics-modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        className="add-metrics-modal"
        style={{ maxWidth: '36rem', maxHeight: '90vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3>Manage Layouts</h3>
          <button
            onClick={handleClose}
            disabled={saving || deletingId}
            className="add-metrics-btn-cancel"
            style={{ padding: '0.25rem', minWidth: 'auto' }}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <FaSpinner className="fa-spin" size={24} />
          </div>
        ) : visibleLayouts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            <p>No layouts available to manage.</p>
            {!isAdmin && <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>You can see your own layouts and public layouts.</p>}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {visibleLayouts.map((layout) => (
              <div
                key={layout.id}
                className="manage-layouts-card"
              >
                {editingId === layout.id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                      disabled={saving}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEdit(layout.id);
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => handleSaveEdit(layout.id)}
                      disabled={saving || !editName.trim()}
                      className="add-metrics-btn-apply"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className="add-metrics-btn-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="manage-layouts-card-header">
                        <span style={{ fontWeight: 500 }}>{layout.name}</span>
                        {layout.is_public ? (
                          <FaUsers size={14} title="Shared / Public" className="manage-layouts-icon" />
                        ) : (
                          <FaLock size={14} title="Private" className="manage-layouts-icon" />
                        )}
                      </div>
                      <div className="manage-layouts-card-meta">
                        Created: {layout.created_at ? new Date(layout.created_at).toLocaleDateString() : 'N/A'}
                        {layout.author_id === userId && <span className="manage-layouts-your">• Your layout</span>}
                        {layout.is_public && layout.author_id !== userId && (
                          <span className="manage-layouts-public">• Public layout {!isAdmin && '(Read-only)'}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      {(isAdmin || layout.author_id === userId) && (
                        <button
                          type="button"
                          onClick={() => handleStartEdit(layout)}
                          disabled={saving || deletingId || (layout.is_public && !isAdmin && layout.author_id !== userId)}
                          className="manage-layouts-btn"
                          title="Rename"
                        >
                          <FaEdit size={14} />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => handleTogglePublic(layout)}
                          disabled={saving || deletingId}
                          className="manage-layouts-btn"
                          title={layout.is_public ? 'Make private' : 'Make public'}
                        >
                          {layout.is_public ? <FaLock size={14} /> : <FaUsers size={14} />}
                        </button>
                      )}
                      {(isAdmin || layout.author_id === userId) && (
                        <button
                          type="button"
                          onClick={() => handleDelete(layout.id, layout.name)}
                          disabled={saving || deletingId === layout.id || (layout.is_public && !isAdmin && layout.author_id !== userId)}
                          className="manage-layouts-btn manage-layouts-btn-delete"
                          title="Delete"
                        >
                          {deletingId === layout.id ? (
                            <FaSpinner className="fa-spin" size={14} />
                          ) : (
                            <FaTrash size={14} />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
